import { ChangeEvent, useCallback, useMemo, useState } from 'react'

import dayjs, { Dayjs } from 'dayjs'

import { useCupons, useStudents } from '../context'
import { NewCupon, Student } from '../types'

const INITIAL_STATE: NewCupon = {
  idStudent: '',
  amountOfClasses: 0,
  price: 0,
  initDate: dayjs(dayjs()).format('YYYY-MM-DD'),
  dueDate: dayjs(dayjs()).add(30, 'day').format('YYYY-MM-DD'),
  attendance: 0,
  payed: false,
  teacher: '',
}

export const useBonus = () => {
  const { studentsList } = useStudents()
  const { cupons } = useCupons()
  const [hasActiveBonus, setHasActiveBonus] = useState(false)
  const now = useMemo(() => dayjs(), [])

  const [newCupon, setNewCupon] = useState<NewCupon>(INITIAL_STATE)

  const updateCupon = useCallback(
    <K extends keyof NewCupon>(key: K, value: NewCupon[K]) => {
      setNewCupon((prev) => ({
        ...prev,
        [key]: value,
      }))
    },
    [],
  )

  const updateInput = useCallback(
    (e: ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
      const { name, value } = e.target

      setNewCupon((prev) => ({
        ...prev,
        [name]: value,
      }))
    },
    [],
  )

  const updateDate = useCallback(
    (newDate: Dayjs | null) => {
      const baseDate = newDate ?? dayjs(newCupon.initDate)
      const isSingleClass = newCupon.amountOfClasses === 1
      const _initDate = baseDate.format('YYYY-MM-DD')
      const _endDate = isSingleClass
        ? _initDate
        : baseDate.add(30, 'day').format('YYYY-MM-DD')

      setNewCupon((prev) => ({
        ...prev,
        initDate: _initDate,
        dueDate: _endDate,
      }))
    },
    [newCupon.amountOfClasses, newCupon.initDate],
  )

  const handleStudentChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
      const { value } = e.target
      const selectedStudent = studentsList?.find(
        (student) => student.uid === value,
      )

      if (selectedStudent) {
        updateStudent(selectedStudent)
      }
    },
    [studentsList],
  )

  //   const calculateBonusAttendance = useCallback(
  //     (
  //       currentAmountOfClasses: number,
  //       currentAttendance: number,
  //       hasActiveBonus: boolean | null,
  //       activeBonus: string | null,
  //     ) => {
  //       let currentDebt = 0
  //       let attendance = 0
  //       const activeCupon = cupons.find((cupon) => cupon.uid === activeBonus)
  //       setStudentWidthActiveCupon(false)

  //       currentDebt = currentAmountOfClasses - currentAttendance

  //       if (!hasActiveBonus) {
  //         if (currentAmountOfClasses < currentAttendance) {
  //           // bono vencido y debe clases => bono.amountClasses - currentDebt
  //           attendance = Math.abs(currentDebt)
  //           // console.log(`bono: INACTIVO VENCIDO y debe ${currentDebt} clases`)
  //         }
  //       } else {
  //         if (dayjs(activeCupon?.dueDate).isAfter(now)) {
  //           if (currentDebt > 0) {
  //             attendance = 0
  //             // bono vigente y 0 deuda => 0
  //             setStudentWidthActiveCupon(true)
  //             console.log(
  //               `bono: ACTIVO VIGENTE y tiene ${currentDebt} clases a favor => bono 1 clase`,
  //             )
  //           } else if (currentDebt === 0) {
  //             attendance = 0
  //             // bono vigente y clases a favor => bono de 1 clase
  //             // console.log(
  //             //   `bono: ACTIVO VIGENTE y ${currentDebt} clases => bono nuevo`,
  //             // )
  //           } else {
  //             attendance = Math.abs(
  //               newCupon.amountOfClasses - Math.abs(currentDebt),
  //             )
  //             // bono vigente y deuda => bono.amountClasses - currentDebt
  //             // console.log(`bono: ACTIVO VIGENTE y debe ${currentDebt}`)
  //           }
  //         } else {
  //           // bono vencido y 0 deuda => 0

  //           attendance = 0
  //           // console.log(`bono: ACTIVO VENCIDO y clases ${currentDebt}`)
  //         }
  //       }

  //       console.log(
  //         `attendance: ${attendance}, cupon classes: ${newCupon.amountOfClasses}`,
  //       )
  //       return attendance
  //     },
  //     [newCupon.amountOfClasses, newCupon.idStudent],
  //   )
  const calculateBonusAttendance = useCallback(
    (
      currentAmountOfClasses: number,
      currentAttendance: number,
      hasActiveBonusFlag: boolean | null,
      activeBonusId: string | null,
    ) => {
      const currentDebt = currentAmountOfClasses - currentAttendance
      let attendance = 0

      const activeCupon = activeBonusId
        ? cupons.find((cupon) => cupon.uid === activeBonusId)
        : undefined

      setHasActiveBonus(false)

      if (!hasActiveBonusFlag) {
        if (currentAmountOfClasses < currentAttendance) {
          // bono vencido y debe clases => bono.amountClasses - currentDebt
          attendance = Math.abs(currentDebt)
        }
      } else {
        const isBonusStillValid =
          activeCupon && dayjs(activeCupon.dueDate).isAfter(now)

        if (isBonusStillValid) {
          if (currentDebt > 0) {
            // bono vigente y 0 deuda => 0
            attendance = 0
            setHasActiveBonus(true)
          } else if (currentDebt === 0) {
            attendance = 0
          } else {
            attendance = Math.abs(
              newCupon.amountOfClasses - Math.abs(currentDebt),
            )
          }
        } else {
          // bono vencido y 0 deuda => 0
          attendance = 0
        }
      }

      return attendance
    },
    [cupons, now, newCupon.amountOfClasses],
  )

  const updateStudent = useCallback(
    (student: Student) => {
      const {
        uid,
        currentAmountOfClasses,
        currentAttendance,
        hasActiveBonus: hasActiveBonusFlag,
        activeBonus,
      } = student

      const attendance = calculateBonusAttendance(
        currentAmountOfClasses,
        currentAttendance,
        hasActiveBonusFlag,
        activeBonus,
      )

      setNewCupon({
        ...INITIAL_STATE,
        idStudent: uid,
        attendance,
      })
    },
    [calculateBonusAttendance],
  )

  const resetCupon = useCallback(() => {
    setNewCupon(INITIAL_STATE)
    setHasActiveBonus(false)
  }, [INITIAL_STATE])

  return {
    newCupon,
    updateCupon,
    updateInput,
    handleStudentChange,
    updateStudent,
    updateDate,
    studentWithActiveCupon: hasActiveBonus,
    resetCupon,
  }
}
